import { useQuery } from '@tanstack/react-query'
import { api, apiMethods } from '../services/api'
import { BookOpen, CheckCircle, UserCheck } from 'lucide-react'
import { useLanguageStore } from '../stores/languageStore'

function hasGoogleAdsConnection(lead) {
  if (typeof lead?.has_google_ads_connection === 'boolean') {
    return lead.has_google_ads_connection
  }

  return [lead?.gclid, lead?.gbraid, lead?.wbraid].some(
    (value) => typeof value === 'string' && value.trim() !== '',
  )
}

async function fetchLeadStageStats() {
  const perPage = 100
  let page = 1
  let hasNextPage = true
  let adsLeads = 0
  let qualified = 0
  let converted = 0

  while (hasNextPage) {
    const response = await apiMethods.getLeads({
      page,
      per_page: perPage,
    })

    const leads = response?.data || []
    const meta = response?.meta || {}
    const currentPage = Number(meta.current_page) || page
    const lastPage = Number(meta.last_page)

    for (const lead of leads) {
      if (hasGoogleAdsConnection(lead)) {
        adsLeads += 1
      }

      if (lead?.current_stage === 'qualified') {
        qualified += 1
      }

      if (lead?.current_stage === 'won') {
        converted += 1
      }
    }

    if (Number.isFinite(lastPage) && lastPage > 0) {
      hasNextPage = currentPage < lastPage
    } else {
      hasNextPage = leads.length === perPage
    }

    page = currentPage + 1
  }

  return {
    adsLeads,
    qualified,
    converted,
  }
}

export default function Dashboard() {
  const { currentLanguageCode } = useLanguageStore()

  const { data: languages } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.get('/languages').then((res) => res.data),
  })

  const currentLanguage = languages?.data?.find(
    (lang) => lang.code === currentLanguageCode
  )

  const { data: posts } = useQuery({
    queryKey: ['posts', currentLanguageCode],
    queryFn: () =>
      api
        .get('/posts', {
          params: { locale: currentLanguageCode || 'en' },
        })
        .then((res) => res.data),
  })

  const { data: leadStats } = useQuery({
    queryKey: ['dashboard-lead-stage-stats'],
    queryFn: fetchLeadStageStats,
    staleTime: 60 * 1000,
  })

  const localeName = currentLanguageCode?.toUpperCase() || ''

  const stats = [
    {
      name: `Total Posts (${localeName})`,
      value: posts?.data?.length || 0,
      icon: BookOpen,
    },
    {
      name: 'Ads Leads',
      value: leadStats?.adsLeads || 0,
      icon: UserCheck,
    },
    {
      name: 'Qualified',
      value: leadStats?.qualified || 0,
      icon: CheckCircle,
    },
    {
      name: 'Converted',
      value: leadStats?.converted || 0,
      icon: CheckCircle,
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Welcome to Ginger CMS. Here's an overview of your content.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-white overflow-hidden shadow rounded-lg"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <stat.icon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stat.value}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Recent Activity
          </h3>
          <div className="mt-5">
            <div className="text-sm text-gray-500">
              No recent activity to display.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
